import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import OwnerNav from '../components/OwnerNav';
import api, { publicShopsAPI } from '../services/api';
import { FitSessionModal as EmbeddedFitSessionModal } from '@fit/modal';
import { Pencil, Check, X } from 'lucide-react';

const ensureServerBase = (maybeApiBase: string): string => {
  const s = String(maybeApiBase || '').trim();
  if (!s) return '';
  if (/\/api\/?$/.test(s)) return s.replace(/\/api\/?$/, '');
  return s;
};

const DEFAULT_SHOP_ID = '1c0536c3-26b8-4844-85cb-d575e30f54e6';

const OwnerFitWidget: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  const [shopId, setShopId] = useState<string>(() => params.get('shopId') || DEFAULT_SHOP_ID);
  const [retailerData, setRetailerData] = useState<{ id: string; shopName: string; logoUrl: string; primaryColor: string; apiKey: string }>({ id: 'portal', shopName: 'Klantportaal', logoUrl: '', primaryColor: '#f97316', apiKey: '' });
  const [brandingHideLogo, setBrandingHideLogo] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftShopId, setDraftShopId] = useState('');
  const [isEditingCust, setIsEditingCust] = useState(false);
  const [customerShopId, setCustomerShopId] = useState<string>(() => params.get('customerShopId') || DEFAULT_SHOP_ID);
  const [draftCustomerShopId, setDraftCustomerShopId] = useState('');

  // Admin ACL based on consumer profile user_type
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await fetch(String(api?.defaults?.baseURL || '').replace(/\/+$/, '') + '/consumer/profile', { credentials: 'include' });
        if (!resp.ok) { setIsAdmin(false); return; }
        const json = await resp.json().catch(() => ({}));
        const userType = json?.profile?.user_type || null;
        setIsAdmin(userType === 'ADMIN');
      } catch {
        setIsAdmin(false);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  // Update URL query when shopId changes
  useEffect(() => {
    try {
      const q = new URLSearchParams(params.toString());
      if (shopId) q.set('shopId', shopId); else q.delete('shopId');
      if (customerShopId) q.set('customerShopId', customerShopId); else q.delete('customerShopId');
      setParams(q, { replace: true });
    } catch {}
  }, [shopId, customerShopId]);

  // Compute server base for widget API calls from axios baseURL
  const serverBase = useMemo(() => ensureServerBase(String(api?.defaults?.baseURL || '')), []);

  // Load branding for current shop using plan-aware backend config
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!shopId) return;
        const resp = await api.get('/widget/branding-config', { params: { shopId } });
        const data = (resp?.data || {}) as any;
        if (!data?.success || !data) return;
        if (cancelled) return;
        setRetailerData(prev => ({
          ...prev,
          id: String(shopId),
          shopName: String(data.shopName || prev.shopName || 'Webshop'),
          logoUrl: String(data.logoUrl || '')
        }));
        setBrandingHideLogo(!!data.brandingHideLogo);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [shopId]);

  if (loading) {
    return (
      <>
        <OwnerNav />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">Laden...</div>
        </div>
      </>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <OwnerNav />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white shadow rounded-lg p-8 max-w-md w-full text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">403 - Geen toegang</h1>
            <p className="text-gray-600 mb-6">Je hebt geen toegang tot de owner tools.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OwnerNav />
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900">FiT-widget</h1>
            <p className="text-gray-600">Direct ingebed in de pagina.</p>
          </div>

          {/* Config veld voor shopId */}
          <div className="bg-white border border-gray-200 rounded-xl shadow p-4 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Shop ID | Owner dashboard</label>
            <div className="flex gap-3 items-center">
              <input
                type="text"
                className="form-input flex-1"
                value={isEditing ? draftShopId : shopId}
                readOnly={!isEditing}
                placeholder="Voer Shop UUID in"
                onChange={isEditing ? (e) => setDraftShopId(e.target.value) : undefined}
              />
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => { setDraftShopId(shopId); setIsEditing(true); }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4" />
                  Bewerken
                </button>
              )}
              {isEditing && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setShopId(draftShopId || ''); setIsEditing(false); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <Check className="h-4 w-4" />
                    Opslaan
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIsEditing(false); setDraftShopId(''); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <X className="h-4 w-4" />
                    Annuleren
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Config veld voor customer shopId */}
          <div className="bg-white border border-gray-200 rounded-xl shadow p-4 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Shop ID | Customer dashboard</label>
            <div className="flex flex-col gap-3">
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  className="form-input flex-1"
                  value={isEditingCust ? draftCustomerShopId : customerShopId}
                  readOnly={!isEditingCust}
                  placeholder="Voer Shop UUID in"
                  onChange={isEditingCust ? (e) => setDraftCustomerShopId(e.target.value) : undefined}
                />
                {!isEditingCust && (
                  <button
                    type="button"
                    onClick={() => { setDraftCustomerShopId(customerShopId); setIsEditingCust(true); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil className="h-4 w-4" />
                    Bewerken
                  </button>
                )}
                {isEditingCust && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setCustomerShopId(draftCustomerShopId || ''); setIsEditingCust(false); }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      <Check className="h-4 w-4" />
                      Opslaan
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsEditingCust(false); setDraftCustomerShopId(''); }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      <X className="h-4 w-4" />
                      Annuleren
                    </button>
                  </div>
                )}
              </div>
              <div className="flex">
                <Link to={`/customer/fit-widget?shopId=${encodeURIComponent(customerShopId || '')}`} className="btn btn-secondary">
                  Open Customer widget
                </Link>
              </div>
            </div>
          </div>

          {/* Embedded widget smaller, modal-achtig */}
          <div className="embedded-fit max-w-3xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden p-0">
              <EmbeddedFitSessionModal
                retailerData={retailerData}
                productData={{ id: '', title: '', price: '', imageUrl: '', url: '' }}
                apiUrl={serverBase}
                category={undefined}
                context={{ shopId: shopId || null, retailerId: null, isPortalEmbed: true }}
                brandingHideLogo={brandingHideLogo}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OwnerFitWidget;
